import React, {Component} from "react";
import "./EmrCheckPanel.css";
import {Button} from "react-bootstrap";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import {Badge, NumberInput, RadioInput, SelectInput, TextareaInput, TextInput} from "../../../../components";
import ObjectUtil from "../../../../utils/ObjectUtil";
import RecommenderService from "../../../../services/recommender-service/RecommenderService";
import RemotingService from "../../../../services/remoting-service/RemotingService";
import Tooltip from "../../../../components/tooltip/Tooltip";
import LoadingSteps from "./LoadingSteps";
import Dictation from "./Dictation";
import GuidelinePanel from "./GuidelinePanel";
import FeedbackBar from "./FeedbackBar";
import AuthService from "../../../../services/auth-service/AuthService";
import {
    addedComposeValues, allComposePaths, buildComposeRows, effectiveComposeValue,
    formatComposeValue, isDestructiveRow, isEmptyComposeValue, unmappedComposePaths
} from "./ComposeFields";

const COMPOSE_STEPS = [
    "Reading the note…",
    "Pulling out onset, character and timing…",
    "Mapping each phrase onto a record field…",
    "Checking the values against the form's options…",
];

const CHECK_STEPS = [
    "Reading this visit…",
    "Matching the record against the billed codes…",
    "Running the claim rules…",
    "Looking for what a payer would deny…",
    "Drafting answers you can accept in one tap…",
];

const CPT_STEPS = [
    "Mapping this visit onto a billing request…",
    "Finding similar visits…",
    "Validating the codes…",
];

// Plain-language consequence labels: "deny" is what happens to the claim, not a severity
// grade, and the clinician should be able to read the stake without decoding jargon.
const CONSEQUENCE_META = {
    deny: {variant: "danger", label: "Can be denied", order: 0},
    downcode: {variant: "warning", label: "Can be downcoded", order: 1},
    audit: {variant: "info", label: "Audit flag", order: 2},
};

// The visit's own tabs, so a finding with no writable field can still send the
// clinician to the right place (finding.section matches these eventKeys 1:1).
// Similar-visit suggestions ignore what was done in this session (they proposed 97112 for a
// visit with no neuromuscular work), so the panel shows only the AI list for now. The
// service still returns both and the feedback snapshot still records both; set
// REACT_APP_RECOMMENDER_SHOW_SIMILAR=true to bring them back.
const SHOW_SIMILAR_VISITS = process.env.REACT_APP_RECOMMENDER_SHOW_SIMILAR === "true";
const SIMILAR_SOURCE = "Similar visits";

const SECTION_TAB = {
    subjective: {key: "subjective", label: "Subjective"},
    assessment: {key: "assessment", label: "Assessment"},
    diagnosis: {key: "diagnosis", label: "Diagnosis & Billing"},
    treatment: {key: "treatment", label: "Treatment"},
};

const SECTION_TAB_LABEL = Object.fromEntries(
    Object.values(SECTION_TAB).map(t => [t.key, t.label]));

// The recommender reports what it lacks as API field names (`clinical_impression`). The
// clinician has never seen those words: name the note that is missing, say why the codes
// depend on it, and point at the tab where it is written.
const REQUIRED_FIELD_META = {
    complaint: {
        label: "Chief complaint",
        tab: "subjective",
        hint: "What brought the patient in — the codes are chosen against it.",
    },
    clinical_impression: {
        label: "Clinical impression",
        tab: "diagnosis",
        hint: "Your assessment of what is going on. Without it there is nothing to justify a "
            + "procedure, so no code can be suggested.",
    },
    provider_specialty: {
        label: "Provider speciality",
        tab: null,
        hint: "Comes from the appointment's clinician — if it is missing, the appointment "
            + "itself needs fixing rather than the note.",
    },
    visit_minutes: {
        label: "Visit duration",
        tab: null,
        hint: "Start and end time of the encounter; timed codes are counted from it.",
    },
    diagnoses: {
        label: "Diagnosis",
        tab: "diagnosis",
        hint: "The coded diagnosis the procedures will hang from.",
    },
};

// CPT codes already on the visit: the recommender tab only shows what is missing.
const recordedCptCodes = (visit) => {
    const procedures = [
        ...(ObjectUtil.get(visit, "diagnosis.directBillingProcedures") || []),
        ...(ObjectUtil.get(visit, "diagnosis.selfPayProcedures") || []),
    ];
    const codes = procedures.map(p => String(p?.cpt?.cptCode ?? p?.cpt?.code ?? p?.cpt ?? "").trim());
    (ObjectUtil.get(visit, "diagnosis.diagnosisGroups") || []).forEach(group =>
        (group?.diagnosisItems || []).forEach(item => codes.push(String(item?.code || "").trim())));
    return new Set(codes.filter(Boolean));
};

// `id` is re-assigned to f1..fN on every analyze call, so it cannot identify a finding
// across re-checks. rule_id + target_path can.
// ICD codes nest: M54.5 is the parent of M54.50, and the two engines do not always answer
// at the same depth. Compare with the dot removed so a parent and its child share a key;
// CPT codes have no such hierarchy, so this only ever fires for dotted (ICD) codes.
const codeStem = code => String(code || "").replace(/\./g, "").toUpperCase();

const isMoreSpecific = (code, against) => codeStem(code).length > codeStem(against).length;

// The key an incoming code should merge into: an existing code that is a prefix of it (its
// parent), or that it is a prefix of (its child). Falls back to the code itself.
const mergeKeyFor = (code, merged) => {
    if (!code.includes(".")) return code;
    const stem = codeStem(code);
    for (const [key, row] of merged) {
        if (!String(row.code || key).includes(".")) continue;
        const other = codeStem(row.code || key);
        if (stem.startsWith(other) || other.startsWith(stem)) return key;
    }
    return code;
};

// The service states a gap as an absence ("no cervical findings"); after "missing" that
// reads as a double negative.
const gapText = gap => String(gap).replace(/^no\s+/i, "");

const findingKey = f => `${f.rule_id}|${f.target_path || ""}`;

// Select options arrive as enum keys ("IMPROVING"); the chip shows "Improving", the
// answer stays the key.
const optionLabel = o => /^[A-Z0-9_]+$/.test(String(o))
    ? String(o).charAt(0) + String(o).slice(1).toLowerCase().replace(/_/g, " ")
    : String(o);

// "Doesn't apply" is a clinical decision about this visit; it used to vanish on reload and
// the same card came back. Kept per visit in this browser (a server-side record, for the
// audit trail, is the follow-up).
const skipStorageKey = visitId => `emr-copilot-skipped:${visitId}`;
const loadSkipped = (visitId) => {
    if (visitId == null) return {};
    try {
        const stored = JSON.parse(window.localStorage.getItem(skipStorageKey(visitId)) || "{}");
        return stored && typeof stored === "object" && !Array.isArray(stored) ? stored : {};
    } catch (e) {
        return {};
    }
};
const saveSkipped = (visitId, skipped) => {
    if (visitId == null) return;
    try {
        if (Object.keys(skipped).length) {
            window.localStorage.setItem(skipStorageKey(visitId), JSON.stringify(skipped));
        } else {
            window.localStorage.removeItem(skipStorageKey(visitId));
        }
    } catch (e) {
        // Storage blocked (private mode): the skip still holds for this session.
    }
};

const isEmpty = v => v === null || v === undefined || v === "" || (Array.isArray(v) && v.length === 0);

// Distinguish "service is down" from "the service rejected our request" -- otherwise a
// contract mismatch (e.g. a 422 on the payload) reads as a connectivity problem.
const errorMessage = (err) => {
    const status = err?.response?.status;
    if (!status) return "Could not reach the recommender service.";
    return `Recommender returned ${status}.`;
};

const formatValue = value => {
    if (isEmpty(value)) return "—";
    if (Array.isArray(value)) return value.join(", ");
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
};

// One-tap negative confirmations. These are not claim findings — they are the
// "confirm none" half of the app's own missing-points list (MissingDataFirstVisit.js),
// which the checkers satisfy with a `none: true` flag on the parent object
// (GenericChecker.isNone) or `redFlagNormal` for red flags. They cost the clinician a
// single tap, so they are kept out of the ranked findings and never counted as questions.
// `apply` mirrors what the corresponding modal writes, including wiping the item list.
// FE-9: why a rule does not apply. Fixed choices first because a reason nobody types is a
// reason nobody can analyse later; the free-text box stays for the case none of them fit.
const SKIP_REASONS = [
    "Not relevant to this patient",
    "Documented elsewhere in the record",
    "Not required by this payer",
];

const CONFIRM_NONE = [
    {
        key: "allergy",
        label: "No known allergies",
        open: v => !ObjectUtil.get(v, "subjective.allergy.none")
            && isEmpty(ObjectUtil.get(v, "subjective.allergy.items")),
        apply: v => {
            const existing = ObjectUtil.get(v, "subjective.allergy") || {};
            ObjectUtil.set(v, ["subjective", "allergy"], {id: existing.id, none: true});
        },
    },
    {
        key: "medicationUse",
        label: "No medications",
        open: v => !ObjectUtil.get(v, "subjective.medicationUse.none")
            && isEmpty(ObjectUtil.get(v, "subjective.medicationUse.items")),
        apply: v => {
            const existing = ObjectUtil.get(v, "subjective.medicationUse") || {};
            ObjectUtil.set(v, ["subjective", "medicationUse"], {id: existing.id, none: true, items: []});
        },
    },
    {
        key: "surgicalHistory",
        label: "No surgeries",
        open: v => !ObjectUtil.get(v, "subjective.surgicalHistory.none")
            && isEmpty(ObjectUtil.get(v, "subjective.surgicalHistory.items")),
        apply: v => {
            const existing = ObjectUtil.get(v, "subjective.surgicalHistory") || {};
            ObjectUtil.set(v, ["subjective", "surgicalHistory"], {id: existing.id, none: true, items: []});
        },
    },
    {
        key: "medicalHistory",
        label: "No significant medical history",
        open: v => !ObjectUtil.get(v, "subjective.medicalHistory.none")
            && isEmpty(ObjectUtil.get(v, "subjective.medicalHistory.diagnosisItems")),
        apply: v => {
            const existing = ObjectUtil.get(v, "subjective.medicalHistory") || {};
            ObjectUtil.set(v, ["subjective", "medicalHistory"], {
                id: existing.id, none: true, items: [],
                // the vaccination answers live on this object and are unrelated to the flag
                vaccinationHistory: existing.vaccinationHistory,
                vaccinationHistoryDetail: existing.vaccinationHistoryDetail,
            });
        },
    },
    {
        key: "familyHistory",
        label: "No significant family history",
        open: v => !ObjectUtil.get(v, "subjective.familyHistory.none")
            && isEmpty(ObjectUtil.get(v, "subjective.familyHistory.items")),
        apply: v => {
            const existing = ObjectUtil.get(v, "subjective.familyHistory") || {};
            ObjectUtil.set(v, ["subjective", "familyHistory"], {id: existing.id, none: true, items: []});
        },
    },
    {
        key: "redFlag",
        label: "No red flags",
        open: v => ObjectUtil.get(v, "subjective.complaint.redFlagNormal") !== true
            && isEmpty(ObjectUtil.get(v, "subjective.complaint.redFlag")),
        apply: v => ObjectUtil.set(v, ["subjective", "complaint", "redFlagNormal"], true),
    },
];

export default class EmrCheckPanel extends Component {

    constructor(props) {
        super(props);
        this.state = {
            // The drawer opens on the writing step, not the checking one: a note has to
            // exist before there is anything worth checking. The Check tab carries the
            // finding count in its title so the badge on the button stays honest.
            activeTab: "draft",
            fixed: {},            // finding key -> label of the fix option applied
            wide: true,           // the note textarea needs the room; toggled in the header
            // One draft box per record section, expanded one at a time; the guideline
            // panel follows whichever is open. The LLM routes misplaced content anyway —
            // the split is a prompt to the clinician, not a constraint on the parse.
            draftSections: {subjective: "", objective: "", treatment: ""},
            activeDraftSection: "subjective",
            composedPaths: [],    // leaf paths the last parse yielded — the guideline
                                  // panel's "captured" tier; keyword matches are live
            composing: false,
            composeSections: null,   // [{title, rows:[{path,label,oldValue,newValue,selected}]}]
            composeUnmapped: [],
            composeNoSlot: [],
            composeNote: "",
            loading: false,
            error: null,
            analysis: null,
            answers: {},          // findingKey -> value typed by the clinician
            answerBase: {},       // findingKey -> what the record already held when we asked,
                                  // so an answer edits the existing text instead of replacing it
            answeredBefore: {},   // findingKey -> the answer given on a previous pass, kept so a
                                  // rule that fails again says so instead of asking from scratch
            accepted: {},         // findingKey -> bool, for suggestions
            confirmed: {},        // CONFIRM_NONE key -> bool
            skipped: loadSkipped(props.currentVisit?.id),  // rule_id -> reason: rules the clinician says do not apply
            // What the clinician has told the panel this session, sent with every analyze so
            // a later check treats it as true instead of asking again or contradicting it.
            facts: [],
                                  // to this patient. Sent on every analyze, so the reasons
                                  // land in the service's event log with the call.
            skipping: null,       // rule_id whose reason is currently being picked
            bulkText: "",         // FE-10: one answer to all the open questions at once
            bulkOpen: false,
            bulkLoading: false,
            bulkError: null,
            bulkUnanswered: [],
            expanded: {},         // findingKey -> bool, overrides the severity default
            applied: false,       // something was written but not saved yet
            recommendLoading: false,
            recommendError: null,
            recommendation: null,
            cptIndex: null,       // cptCode -> {cpt, billType}, from this clinic's catalogue
            catalogLoading: false,
            addError: null,
            topOffset: 0,         // where the app header ends, so the drawer clears the menu
        };
    }

    componentDidMount() {
        // Seed the note with the complaint already on the record so dictation continues it
        // instead of starting a second, competing account of the same visit.
        const recorded = ObjectUtil.get(this.props.currentVisit, "subjective.complaint.complaint");
        if (!isEmptyComposeValue(recorded)) {
            // Trailing newline: typed text used to run straight on ("...work shiftLow back pain").
            this.setState(state => ({draftSections: {...state.draftSections, subjective: `${recorded}\n`}}));
        }

        this.runAnalyze();
        document.addEventListener("keydown", this.onKeyDown);
        // The app's top navigation is static (it scrolls away), so a drawer pinned to
        // top:0 would cover the menu. Track where the header actually ends instead.
        this.trackHeaderOffset();
        window.addEventListener("scroll", this.trackHeaderOffset, true);
        window.addEventListener("resize", this.trackHeaderOffset);
    }

    componentWillUnmount() {
        document.removeEventListener("keydown", this.onKeyDown);
        window.removeEventListener("scroll", this.trackHeaderOffset, true);
        window.removeEventListener("resize", this.trackHeaderOffset);
        if (this.offsetFrame) cancelAnimationFrame(this.offsetFrame);
    }

    trackHeaderOffset = () => {
        if (this.offsetFrame || this.props.hidden) return;
        this.offsetFrame = requestAnimationFrame(() => {
            this.offsetFrame = null;
            const header = document.querySelector(".Wrapper > .Header");
            const top = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
            if (top !== this.state.topOffset) this.setState({topOffset: top});
        });
    };

    onKeyDown = (event) => {
        if (event.key === "Escape" && !this.props.hidden) this.requestClose();
    };

    // Closing is free: EMR.js keeps this component mounted once it has been opened, so the
    // note, the dictation and the reviewed parse are all still here on the way back in.
    // Nothing is lost, so nothing is asked — the old "Discard note?" prompt was guarding an
    // unmount that no longer happens.
    requestClose = () => {
        if (this.props.onClose) this.props.onClose();
    };

    componentDidUpdate(prevProps) {
        // EMR.js reloads the record after a save; re-check against the fresh visit. A closed
        // drawer stays mounted now, so the re-check is deferred rather than fired at a panel
        // no one is looking at — the visit can be saved several times while it sits closed.
        if (prevProps.currentVisit !== this.props.currentVisit) {
            this.setState({applied: false, recommendation: null});
            if (this.props.hidden) {
                this.analyzeStale = true;
            } else {
                this.runAnalyze();
                // The reload also threw the code list away. Sitting on the CPT tab while it
                // happened used to leave the panel showing nothing at all, because that tab
                // only fetches when it is switched TO — this is the blank screen after a save.
                if (this.state.activeTab === "cpt") this.loadRecommendation();
            }
        }

        if (prevProps.hidden && !this.props.hidden) {
            // The offset was not tracked while closed; the page may have scrolled since.
            this.trackHeaderOffset();
            if (this.analyzeStale) {
                this.analyzeStale = false;
                this.runAnalyze();
            }
        }
    }

    runAnalyze = () => {
        const {emr, currentVisit} = this.props;
        if (!emr || !currentVisit) return;

        this.setState({loading: true, error: null});
        RecommenderService.analyze(
            {record: emr, visitId: currentVisit.id, skipped: this.skipPayload(), facts: this.state.facts},
            (data) => this.setState(state => {
                // A "bill X instead" option can only be offered once the catalogue says X exists.
                if ((data.findings || []).some(f => (f.fix_options || []).some(o => o.type === "replace_code"))) {
                    setTimeout(this.loadCptCatalog, 0);
                }
                const seeds = this.seedAnswers(data.findings);
                // Whatever is still in `answers` here was typed but never applied (apply()
                // empties it). Keep those drafts on cards that are still open -- a re-check
                // triggered by a one-tap fix used to wipe them.
                const live = new Set((data.findings || []).map(findingKey));
                const drafts = Object.fromEntries(Object.entries(this.changedAnswers(state))
                    .filter(([key]) => live.has(key)));
                return {
                    loading: false, analysis: data,
                    answers: {...seeds, ...drafts}, answerBase: seeds, skipping: null,
                    accepted: {}, confirmed: {}, expanded: {},
                };
            }),
            (err) => this.setState({loading: false, error: errorMessage(err)})
        );
    };

    loadRecommendation = () => {
        const {emr, currentVisit} = this.props;
        this.setState({recommendLoading: true, recommendError: null});
        RecommenderService.recommend(
            {record: emr, visitId: currentVisit.id},
            (data) => this.setState({recommendLoading: false, recommendation: data}),
            (err) => this.setState({recommendLoading: false, recommendError: errorMessage(err)})
        );
    };

    // --- Draft: free text (typed or dictated) -> structured record fields ---

    // Dictation lands in whichever section is open — switching the accordion switches
    // where the words go, which is the whole "one window, three boxes" flow.
    appendDictated = (chunk) => {
        if (!chunk) return;
        this.setState(state => {
            const key = state.activeDraftSection;
            const existing = state.draftSections[key] || "";
            const separator = !existing || /\s$/.test(existing) ? "" : " ";
            return {draftSections: {...state.draftSections, [key]: `${existing}${separator}${chunk}`}};
        });
    };

    setDraftSection = (key, value) => {
        this.setState(state => ({draftSections: {...state.draftSections, [key]: value}}));
    };

    // The three boxes are a writing aid, not three requests: the parse gets one text,
    // with headings, and the scribe prompt routes misplaced facts to the right fields.
    combinedDraftText = () => {
        const {subjective, objective, treatment} = this.state.draftSections;
        return [
            subjective.trim() && `SUBJECTIVE\n${subjective.trim()}`,
            objective.trim() && `OBJECTIVE\n${objective.trim()}`,
            treatment.trim() && `TREATMENT\n${treatment.trim()}`,
        ].filter(Boolean).join("\n\n");
    };

    // Composed from the note alone — the record is deliberately NOT sent. The review list
    // is only meaningful if emr_json is a parse of what was just written; passing the
    // record would have the service echo back fields nobody dictated.
    generateFromDraft = () => {
        const text = this.combinedDraftText();
        if (!text) return;

        this.setState({composing: true, composeNote: "", composeSections: null, composeUnmapped: [], composeNoSlot: []});
        RecommenderService.compose(
            {text},
            (result) => {
                const emrJson = result?.emr_json || {};
                const {sections, matched, notReturned} = buildComposeRows(emrJson, this.props.currentVisit);
                // FE-8: a parse is something to VALIDATE, not to re-enter. Every row starts
                // accepted; the clinician acts only to change or drop one ("I write the text
                // and then I say add, add, add — that feels like double work").
                sections.forEach(section => section.rows.forEach(row => {
                    row.decision = row.decision || "replace";
                }));

                // The note itself is only ever offered when the record has no complaint
                // text yet: an existing complaint is the clinician's own wording and the
                // panel must not overwrite it. Only the Subjective box qualifies — the
                // objective/treatment text is not a complaint.
                // The service distils the complaint ("neck and upper back pain limiting laptop
                // work"); the whole dictated paragraph is only the fallback.
                const subjectiveText = this.state.draftSections.subjective.trim();
                const distilled = String(ObjectUtil.get(emrJson, "subjective.complaint.complaint") || "").trim();
                const recorded = ObjectUtil.get(this.props.currentVisit, "subjective.complaint.complaint");
                if (isEmptyComposeValue(recorded) && (distilled || subjectiveText)) {
                    sections.unshift({
                        title: "Complaint text",
                        tab: "subjective",
                        rows: [{
                            field: {label: "Complaint (free text)"},
                            path: "subjective.complaint.complaint",
                            label: "Complaint (free text)",
                            oldValue: recorded, newValue: distilled || subjectiveText,
                            // Added after the "every row starts accepted" pass above, so it
                            // was left undecided: shown as validated, silently not applied.
                            overwrites: false, multi: false, decision: "replace",
                        }],
                    });
                }

                const rowCount = sections.reduce((sum, s) => sum + s.rows.length, 0);
                this.setState({
                    composing: false,
                    composedPaths: allComposePaths(emrJson),
                    composeSections: sections,
                    composeMatched: matched,
                    composeNotReturned: notReturned,
                    composeUnmapped: unmappedComposePaths(emrJson),
                    composeNoSlot: (result?.unmapped || []).filter(Boolean),
                    composeNote: rowCount ? "" : "Nothing to change — the record already holds what the note says.",
                });
            },
            (err) => this.setState({composing: false, composeNote: errorMessage(err)})
        );
    };

    // Every row is validated to begin with, so an action is a departure from that and
    // clicking it again returns to validated. There is no undecided state any more.
    setComposeRowDecision = (sectionIndex, rowIndex, decision) => {
        this.updateComposeRow(sectionIndex, rowIndex, row =>
            ({...row, decision: row.decision === decision ? "replace" : decision}));
    };

    updateComposeRow = (sectionIndex, rowIndex, fn) => {
        this.setState(state => ({
            composeSections: state.composeSections.map((section, si) => si !== sectionIndex ? section : {
                ...section,
                rows: section.rows.map((row, ri) => ri !== rowIndex ? row : fn(row)),
            }),
        }));
    };

    // Only plain values can be corrected in place; a structured value (a list of history
    // entries, say) is validated or dropped, and corrected on the form itself.
    isEditableComposeRow = row => ["string", "number"].includes(typeof row.newValue);

    startComposeEdit = (sectionIndex, rowIndex) =>
        this.updateComposeRow(sectionIndex, rowIndex, row =>
            ({...row, editing: true, draftValue: String(row.newValue ?? "")}));

    setComposeEdit = (sectionIndex, rowIndex, draftValue) =>
        this.updateComposeRow(sectionIndex, rowIndex, row => ({...row, draftValue}));

    cancelComposeEdit = (sectionIndex, rowIndex) =>
        this.updateComposeRow(sectionIndex, rowIndex, row =>
            ({...row, editing: false, draftValue: undefined}));

    // A corrected row is still a validated row — the edit IS the validation ("it was not
    // dull, it was sharp": click edit, fix the word, done).
    commitComposeEdit = (sectionIndex, rowIndex) =>
        this.updateComposeRow(sectionIndex, rowIndex, row => {
            const value = typeof row.newValue === "number" && row.draftValue !== ""
                ? Number(row.draftValue) : row.draftValue;
            const changed = String(value) !== String(row.newValue);
            return {
                ...row, editing: false, draftValue: undefined,
                newValue: isEmpty(value) ? row.newValue : value,
                edited: changed || row.edited,
                decision: "replace",
            };
        });

    validateAllCompose = () =>
        this.setState(state => ({
            composeSections: (state.composeSections || []).map(section => ({
                ...section,
                rows: section.rows.map(row => ({...row, decision: "replace", editing: false})),
            })),
        }));

    acceptedComposeRows = () =>
        (this.state.composeSections || []).flatMap(section =>
            section.rows.filter(row => row.decision && row.decision !== "discard"));

    // Accepted values go straight into currentVisit — the same object the visit tabs render
    // from — and then the panel moves to Check and re-analyzes. A dictated note always
    // leaves holes; showing what is still missing is the other half of the same job.
    applyCompose = () => {
        const rows = this.acceptedComposeRows();
        rows.forEach(row => {
            const path = row.path.split(".");
            ObjectUtil.set(this.props.currentVisit, path, effectiveComposeValue(row));
            // Exam lists sit next to a "none" (= normal) switch; findings mean it is not normal.
            // Special tests have no such switch (the form keys off the list itself).
            if (path[0] === "assessment" && path[path.length - 1] === "items"
                && path[1] !== "specialTests") {
                ObjectUtil.set(this.props.currentVisit, [...path.slice(0, -1), "none"], false);
            }
        });

        this.setState({
            // The note just changed the record the code list was computed from.
            recommendation: rows.length > 0 ? null : this.state.recommendation,
            composeSections: null,
            composeMatched: 0,
            composeNotReturned: [],
            composeUnmapped: [],
            composeNoSlot: [],
            composeNote: `${rows.length} field(s) applied`,
            applied: rows.length > 0 || this.state.applied,
            activeTab: rows.length > 0 ? "check" : this.state.activeTab,
        });

        if (rows.length > 0) {
            if (this.props.onApplied) this.props.onApplied();
            this.runAnalyze();
        }
    };

    discardCompose = () => {
        this.setState({
            composeSections: null, composeMatched: 0, composeNotReturned: [], composeUnmapped: [], composeNoSlot: [],
            composeNote: "Parse discarded",
        });
    };

    onSelectTab = (key) => {
        this.setState({activeTab: key});
        // A code added on the CPT tab changes which billing rules apply; the check must not
        // keep showing the verdict for the claim as it was before.
        if (key === "check" && this.codesChanged) {
            this.codesChanged = false;
            this.runAnalyze();
        }
        if (key === "cpt") {
            if (!this.state.recommendation && !this.state.recommendLoading) this.loadRecommendation();
            this.loadCptCatalog();   // in parallel: rows need it to know if a code is addable
        }
    };

    // BUG-4: the answer to "make the chief complaint specific" was written straight over
    // the complaint, so each round replaced what the round before had added -- the log shows
    // one visit walking from "Turning head while driving/reversing" to "Desk work" while the
    // same rules kept failing. Free-text answers now open pre-filled with what is on the
    // record: the clinician edits, and a field is only written when the text actually changed.
    seedAnswers = (findings) => {
        const seeds = {};
        (findings || []).forEach(f => {
            if (f.kind === "suggestion" || !f.target_path) return;
            if (f.input_type !== "text" && f.input_type !== "textarea") return;
            const current = ObjectUtil.get(this.props.currentVisit, f.target_path);
            if (typeof current === "string" && current.trim()) seeds[findingKey(f)] = current;
        });
        return seeds;
    };

    // What the clinician actually changed in this pass, carried into the next one so a
    // reappearing finding can admit that the answer did not satisfy the rule.
    changedAnswers = (state = this.state) => {
        const changed = {};
        Object.entries(state.answers || {}).forEach(([key, value]) => {
            if (!isEmpty(value) && value !== state.answerBase[key]) changed[key] = value;
        });
        return changed;
    };

    currentValueOf = (finding) => {
        if (!finding.target_path) return undefined;
        return ObjectUtil.get(this.props.currentVisit, finding.target_path);
    };

    // Add a drafted phrase to whatever the box already holds (the record's own words plus
    // anything typed since), never in place of it.
    appendDraft = (finding, draft) => {
        const key = findingKey(finding);
        const existing = String(this.state.answers[key] || "");
        if (existing.includes(draft)) return;
        // Drafts are often the whole edited field ("Neck pain affecting sleep" for "Neck
        // pain"); appending those wrote the complaint twice.
        const norm = t => t.toLowerCase().replace(/[\s.;,]+/g, " ").trim();
        if (!existing.trim() || norm(draft).includes(norm(existing))) {
            this.setAnswer(finding, draft);
            return;
        }
        const separator = !existing || /[\s]$/.test(existing) ? "" : (/[.;:,]$/.test(existing) ? " " : ". ");
        this.setAnswer(finding, existing + separator + draft);
    };

    // The first keystroke makes a question "settled", and a settled card collapses — which
    // unmounted the box mid-word and left one letter on the record ("M" for an impression).
    // A card being typed in stays open until the clinician closes it.
    setAnswer = (finding, value) => {
        const key = findingKey(finding);
        this.setState(state => ({
            answers: {...state.answers, [key]: value},
            expanded: state.expanded[key] === undefined
                ? {...state.expanded, [key]: true} : state.expanded,
        }));
    };

    // FE-10: "could it be a chat rather than so many back and forth" — ask everything at
    // once and let the clinician reply in their own words. The reply is routed onto the open
    // findings by the service and lands in the same answer boxes, so nothing is written
    // without the clinician seeing it first.
    // Only findings that own a field can take an answer. One without a target_path (a billed
    // code the notes do not support) is fixed on its tab; routing text onto it produced
    // advice nothing could apply, and the card then read as settled.
    openQuestions = () => (this.state.analysis?.findings || [])
        .filter(f => f.kind !== "suggestion" && f.target_path && !f.blocked_by?.length
            && !this.isSettled(f));

    answerAll = () => {
        const text = this.state.bulkText.trim();
        const questions = this.openQuestions();
        if (!text || questions.length === 0) return;

        this.setState({bulkLoading: true, bulkError: null});
        RecommenderService.answerAll(
            {
                record: this.props.emr,
                visitId: this.props.currentVisit.id,
                text,
                findings: questions.map(f => ({
                    rule_id: f.rule_id,
                    target_path: f.target_path,
                    question: f.question,
                    input_type: f.input_type,
                    options: f.options || [],
                })),
            },
            (data) => this.setState(state => {
                const answers = {...state.answers};
                (data.answers || []).forEach(a => {
                    const finding = questions.find(f => f.rule_id === a.rule_id);
                    if (finding) answers[findingKey(finding)] = a.value;
                });
                return {
                    answers,
                    bulkLoading: false,
                    bulkText: "",
                    bulkOpen: (data.unanswered || []).length > 0,
                    bulkUnanswered: data.unanswered || [],
                };
            }),
            (err) => this.setState({bulkLoading: false, bulkError: errorMessage(err)}),
        );
    };

    appendBulkDictation = (chunk) => {
        if (!chunk) return;
        this.setState(state => {
            const separator = !state.bulkText || /\s$/.test(state.bulkText) ? "" : " ";
            return {bulkText: state.bulkText + separator + chunk};
        });
    };

    // Nothing hard-blocks: "I should be able to say this one doesn't apply and skip."
    // The rule leaves the asks and the working score, but the honest score still counts it
    // and the reason travels with every later analyze call.
    skipFinding = (finding, reason) => {
        this.setState(state => ({
            skipped: {...state.skipped, [finding.rule_id]: reason},
            skipping: null,
        }), () => {
            saveSkipped(this.props.currentVisit?.id, this.state.skipped);
            this.runAnalyze();
        });
    };

    unskipRule = (ruleId) => {
        this.setState(state => {
            const skipped = {...state.skipped};
            delete skipped[ruleId];
            return {skipped};
        }, () => {
            saveSkipped(this.props.currentVisit?.id, this.state.skipped);
            this.runAnalyze();
        });
    };

    skipPayload = () =>
        Object.entries(this.state.skipped).map(([rule_id, reason]) => ({rule_id, reason}));

    toggleAccepted = (finding) => {
        const key = findingKey(finding);
        this.setState(state => ({accepted: {...state.accepted, [key]: !state.accepted[key]}}));
    };

    // Collect the writable [path, value] pairs the clinician actually filled in / accepted.
    pendingWrites = () => this.pendingAnswers().map(({finding, value}) => [finding.target_path, value]);

    pendingAnswers = () => {
        const findings = this.state.analysis?.findings || [];
        const answers = [];

        findings.forEach(f => {
            if (!f.target_path) return;    // nothing to round-trip into; shown as a manual note
            const key = findingKey(f);
            if (f.kind === "suggestion") {
                if (this.state.accepted[key]) answers.push({finding: f, value: f.proposed_value});
            } else {
                const value = this.state.answers[key];
                // Unchanged text is not an answer: writing it back would be a no-op at best
                // and, on a field another finding also targets, a way to lose an edit.
                if (!isEmpty(value) && value !== this.state.answerBase[key]) {
                    answers.push({finding: f, value});
                }
            }
        });

        return answers;
    };

    // Newest answer per rule wins; the service keeps only the last few anyway.
    withFacts = (facts, added) => {
        const byRule = new Map(facts.map(f => [f.rule_id, f]));
        added.forEach(f => byRule.set(f.rule_id, f));
        return [...byRule.values()];
    };

    factFor = (finding, answer) => ({
        rule_id: finding.rule_id,
        target_path: finding.target_path || undefined,
        question: finding.question || finding.title || finding.field_label || "",
        answer: typeof answer === "string" ? answer : formatValue(answer),
    });

    openConfirmations = () => CONFIRM_NONE.filter(c => c.open(this.props.currentVisit));

    selectedConfirmations = () => this.openConfirmations().filter(c => this.state.confirmed[c.key]);

    pendingCount = () => this.pendingWrites().length + this.selectedConfirmations().length;

    toggleConfirmation = (entry) => {
        this.setState(state => ({confirmed: {...state.confirmed, [entry.key]: !state.confirmed[entry.key]}}));
    };

    // Write every filled-in answer and ticked confirmation into the visit. Returns the state
    // update that records them; the caller decides what runs next.
    writePending = () => {
        const answered = this.pendingAnswers();
        const confirmations = this.selectedConfirmations();
        // currentVisit is the same object reference the EMR tabs render from, so writing
        // here updates the form; EMR.js bumps a key to remount children that captured it.
        answered.forEach(({finding, value}) =>
            ObjectUtil.set(this.props.currentVisit, finding.target_path.split("."), value));
        confirmations.forEach(c => c.apply(this.props.currentVisit));
        const count = answered.length + confirmations.length;
        return state => ({
            applied: state.applied || count > 0,
            recommendation: count > 0 ? null : state.recommendation,
            answers: {}, accepted: {}, confirmed: {},
            // Only answers that reached the record can "still not be enough" on the next pass.
            answeredBefore: {...state.answeredBefore,
                ...Object.fromEntries(answered.map(({finding, value}) => [findingKey(finding), value]))},
            facts: this.withFacts(state.facts,
                answered.map(({finding, value}) => this.factFor(finding, value))),
        });
    };

    apply = (thenSave) => {
        this.setState(this.writePending(), () => {
            if (this.props.onApplied) this.props.onApplied();
            if (thenSave) {
                if (this.props.onSaveRequested) this.props.onSaveRequested();
            } else {
                this.runAnalyze();
            }
        });
    };

    // One-tap answers. Enum options become chips (a dropdown for 3 values is a wasted
    // click), and free-text questions carry model-drafted answers built from the record —
    // a blank textarea is where clinicians abandon the panel.
    renderChips(finding, options, {exact} = {}) {
        const key = findingKey(finding);
        const value = this.state.answers[key];
        return (
            <div className="emr-check-chips">
                {options.map(option => {
                    // An exact chip IS the answer (a select's options), so it replaces. A drafted
                    // phrase is an addition to prose that may already be there -- clicking it used
                    // to wipe the existing complaint, which is half of BUG-4.
                    const selected = exact ? value === option : String(value || "").includes(option);
                    return (
                        <button key={option} type="button"
                                className={`emr-check-chip ${selected ? "selected" : ""}`}
                                onClick={() => exact
                                    ? this.setAnswer(finding, value === option ? undefined : option)
                                    : this.appendDraft(finding, option)}>
                            {exact ? optionLabel(option) : option}
                        </button>
                    );
                })}
                {!exact && <span className="emr-check-chip-hint">or write your own below</span>}
            </div>
        );
    }

    renderInput(finding) {
        const key = findingKey(finding);
        const value = this.state.answers[key];
        const onChange = v => this.setAnswer(finding, v);
        const drafts = finding.answer_options || [];

        switch (finding.input_type) {
            case "select": {
                const options = finding.options || [];
                // Long option lists still deserve a searchable dropdown.
                return options.length > 0 && options.length <= 6
                    ? this.renderChips(finding, options, {exact: true})
                    : <SelectInput value={value ?? ""} options={options} onChange={onChange}/>;
            }
            case "number":
                return <NumberInput value={value ?? ""}
                                    onChange={v => onChange(isEmpty(v) ? v : Number(v))}/>;
            case "bool":
                return <RadioInput value={value === true ? "Yes" : value === false ? "No" : ""}
                                   options={["Yes", "No"]}
                                   onChange={v => onChange(v == null ? undefined : v === "Yes")}/>;
            case "text":
                return <>
                    {drafts.length > 0 && this.renderChips(finding, drafts)}
                    <TextInput value={value ?? ""} onChange={onChange}/>
                </>;
            case "textarea":
            default:
                return <>
                    {drafts.length > 0 && this.renderChips(finding, drafts)}
                    <TextareaInput value={value ?? ""} style={{width: "100%", minHeight: 56}}
                                   onChange={onChange}/>
                </>;
        }
    }

    // One box above the cards: the questions the panel still has, asked together, answered
    // in one go. The cards stay — this fills them in, and the clinician reviews them there.
    renderBulkAnswer() {
        const questions = this.openQuestions();
        if (questions.length < 2) return null;      // one question needs no second way to ask

        const {bulkOpen, bulkText, bulkLoading, bulkError, bulkUnanswered} = this.state;

        if (!bulkOpen) {
            return (
                <button type="button" className="emr-check-bulk-open"
                        onClick={() => this.setState({bulkOpen: true})}>
                    Answer all {questions.length} in one go — type or dictate
                </button>
            );
        }

        return (
            <div className="emr-check-bulk">
                <div className="emr-check-bulk-head">
                    Answer these in your own words — one paragraph is fine:
                </div>
                <ul className="emr-check-bulk-list">
                    {questions.map(f => <li key={findingKey(f)}>{f.question}</li>)}
                </ul>
                <div className="emr-check-draft-wrap">
                    <textarea className="emr-check-bulk-text" autoFocus
                              placeholder="e.g. Lower back pain limiting bending at work, 7/10, four months, no red flags…"
                              value={bulkText}
                              onChange={e => this.setState({bulkText: e.target.value})}/>
                    <Dictation onAppend={this.appendBulkDictation}/>
                </div>
                {bulkUnanswered.length > 0 &&
                    <div className="emr-check-reason mt-1">
                        {bulkUnanswered.length} question(s) were not covered by that answer — they
                        are still open below.
                    </div>}
                {bulkError && <div className="text-danger mt-1">{bulkError}</div>}
                <div className="emr-check-bulk-actions">
                    <Button size="sm" variant="primary" disabled={bulkLoading || !bulkText.trim()}
                            onClick={this.answerAll}>
                        {bulkLoading ? "Reading your answer…" : "Fill the answers"}
                    </Button>
                    <button type="button" className="emr-check-skip-link"
                            onClick={() => this.setState({bulkOpen: false, bulkError: null})}>
                        Answer them one by one instead
                    </button>
                </div>
            </div>
        );
    }

    // One line under the answer box, not a button competing with it: skipping is the
    // exception, and it costs a reason. Picking one re-runs the check immediately, so the
    // clinician sees the score move rather than wondering whether it registered.
    renderSkipControl(finding) {
        if (this.state.skipping === finding.rule_id) {
            return (
                <div className="emr-check-skip-reasons">
                    <span className="emr-check-skip-prompt">Why does it not apply?</span>
                    {SKIP_REASONS.map(reason =>
                        <button key={reason} type="button" className="emr-check-chip"
                                onClick={() => this.skipFinding(finding, reason)}>{reason}</button>)}
                    <button type="button" className="emr-check-skip-link"
                            onClick={() => this.setState({skipping: null})}>Cancel</button>
                </div>
            );
        }

        return (
            <button type="button" className="emr-check-skip-link"
                    onClick={() => this.setState({skipping: finding.rule_id})}>
                Doesn't apply to this patient
            </button>
        );
    }

    // Skipped rules stay visible as a line the clinician can take back — an audit reads
    // "the clinician decided", not "the check went quiet".
    renderSkipped() {
        const entries = Object.entries(this.state.skipped);
        if (entries.length === 0) return null;
        return (
            <div className="emr-check-skipped">
                <div className="emr-check-skipped-head">
                    {entries.length} rule{entries.length === 1 ? "" : "s"} marked not applicable
                </div>
                {entries.map(([ruleId, reason]) =>
                    <div key={ruleId} className="emr-check-skipped-row">
                        <span>{ruleId.replace(/_/g, " ")} — {reason}</span>
                        <button type="button" className="emr-check-skip-link"
                                onClick={() => this.unskipRule(ruleId)}>Undo</button>
                    </div>)}
            </div>
        );
    }

    // A finding is "settled" once the clinician has answered or accepted it: the card
    // collapses to a green one-liner so the list visibly burns down instead of staying
    // a fixed wall of open work.
    isSettled = (finding) => {
        const key = findingKey(finding);
        if (finding.kind === "suggestion") return !!this.state.accepted[key];
        // Nothing here can settle it; only the next analyze, after the tab is fixed.
        if (!finding.target_path) return false;
        const value = this.state.answers[key];
        // A pre-filled box is not an answer until the clinician has actually changed it.
        return !isEmpty(value) && value !== this.state.answerBase[key];
    };

    // Open the money-losing item first. Everything else starts as a one-line summary,
    // except when nothing can deny the claim -- then the worst item still leads.
    isExpanded = (finding, findings) => {
        const key = findingKey(finding);
        const override = this.state.expanded[key];
        if (override !== undefined) return override;
        if (this.isSettled(finding)) return false;
        // Waiting on another answer: opening it invites a fix the other answer may undo.
        if (finding.blocked_by?.length) return false;
        if (finding.consequence === "deny") return true;
        const ready = findings.filter(f => !f.blocked_by?.length);
        const worst = Math.min(...ready.map(f => CONSEQUENCE_META[f.consequence]?.order ?? 2));
        if (CONSEQUENCE_META[finding.consequence]?.order !== worst) return false;
        if (worst < 2) return true;
        // Audit-only list: open just the first open item, so the clinician always sees
        // one answerable input instead of a list of collapsed one-liners and a greyed
        // "Nothing to apply" that reads as stuck.
        const firstOpen = ready.find(f => !this.isSettled(f) && this.state.expanded[findingKey(f)] === undefined);
        return firstOpen ? findingKey(firstOpen) === key : false;
    };

    toggleExpanded = (finding) => {
        const key = findingKey(finding);
        const findings = this.state.analysis?.findings || [];
        const now = this.isExpanded(finding, findings);
        this.setState(state => ({expanded: {...state.expanded, [key]: !now}}));
    };

    // The wide drawer covers most of the page, so the tab it just opened (the billing
    // table, typically) was hidden behind it. Narrow it to leave the fix in view.
    goToTab = (tabKey) => {
        this.setState({wide: false});
        if (this.props.onNavigate) this.props.onNavigate(tabKey);
    };

    // Provenance: which rule produced this ask, what that rule looks for, and whether it
    // was decided in code or by the model. Answers "why am I being asked this?" without
    // taking room on the card.
    renderRuleTooltip(finding) {
        if (!finding.rule_id) return null;
        const meta = CONSEQUENCE_META[finding.consequence] || CONSEQUENCE_META.audit;
        const kind = finding.rule_kind === "semantic" ? "AI-judged rule" : "Deterministic rule";

        const content = (
            <div style={{maxWidth: 300}}>
                <div><strong>{finding.rule_id}</strong> · {kind}</div>
                {finding.rule_checks && <div className="mt-1">{finding.rule_checks}</div>}
                <div className="mt-1">
                    If unfixed: {meta.label.toLowerCase()} (weight {finding.weight}/3)
                </div>
            </div>
        );

        return (
            <Tooltip title={content} placement="left">
                <span className="emr-check-why" onClick={e => e.stopPropagation()}>?</span>
            </Tooltip>
        );
    }

    renderSettledSummary(finding) {
        const key = findingKey(finding);
        const value = finding.kind === "suggestion" ? finding.proposed_value : this.state.answers[key];
        return (
            <div className="emr-check-settled" onClick={() => this.toggleExpanded(finding)}>
                <span className="emr-check-tick">✓</span>
                <span className="emr-check-settled-label">{finding.field_label}</span>
                <span className="emr-check-settled-value">{formatValue(value)}</span>
                <span className="emr-check-edit">edit</span>
            </div>
        );
    }

    renderFinding(finding, findings) {
        const meta = CONSEQUENCE_META[finding.consequence] || CONSEQUENCE_META.audit;
        const key = findingKey(finding);
        const current = this.currentValueOf(finding);
        const expanded = this.isExpanded(finding, findings);

        if (this.isSettled(finding) && !expanded) {
            return <div key={key}>{this.renderSettledSummary(finding)}</div>;
        }

        const tab = SECTION_TAB[finding.section];

        return (
            <div key={key} className={`emr-check-card ${finding.consequence} ${expanded ? "" : "collapsed"}`}>
                <div className="emr-check-head" onClick={() => this.toggleExpanded(finding)}>
                    <span className={`emr-check-dot ${finding.consequence}`}/>
                    <span className="emr-check-label">{finding.field_label}</span>
                    {this.renderRuleTooltip(finding)}
                    <Badge pill variant={meta.variant}>{meta.label}</Badge>
                    <span className="emr-check-caret">{expanded ? "▾" : "▸"}</span>
                </div>

                {finding.blocked_by?.length > 0 &&
                    <div className="emr-check-waiting">
                        Waiting on {finding.blocked_by.map(b => b.label).join(", ")} — answer
                        that first; it can change this one.
                    </div>}

                {!expanded &&
                    <div className="emr-check-teaser">
                        {finding.kind === "suggestion"
                            ? <>→ <span className="emr-check-proposed">{formatValue(finding.proposed_value)}</span></>
                            : finding.question}
                    </div>}

                {expanded && <>
                    {finding.kind === "suggestion" ? (
                        <>
                            {finding.evidence_quote &&
                                <div className="emr-check-evidence">
                                    <span className="emr-check-evidence-tag">from the note</span>
                                    “{String(finding.evidence_quote).replace(/^["“”']+|["“”']+$/g, "")}”
                                </div>}
                            <div className="emr-check-change">
                                <span className="emr-check-old">{formatValue(current)}</span>
                                <span className="emr-check-arrow">→</span>
                                <span className="emr-check-proposed">{formatValue(finding.proposed_value)}</span>
                            </div>
                            {/* No target_path means there is no single field to write into
                                (e.g. "code the documented diagnosis" -> the Diagnosis tab). */}
                            {finding.target_path
                                ? <Button size="sm" block
                                          variant={this.state.accepted[key] ? "success" : "outline-success"}
                                          onClick={() => this.toggleAccepted(finding)}>
                                    {this.state.accepted[key] ? "✓ Accepted" : "Accept this"}
                                </Button>
                                : this.renderGoToTab(tab)}
                        </>
                    ) : (
                        <>
                            <div className="emr-check-question">{finding.question}</div>
                            {finding.target_path &&
                                <div className="emr-check-current">Now: {formatValue(current)}</div>}
                            {/* A rule that fails again on an answer the clinician already gave must
                                say so. Silently re-asking the identical question is what read as
                                "the EMR is stuck" in the demo. */}
                            {finding.conflicts_with &&
                                <div className="emr-check-conflict">
                                    This still does not fit what you answered earlier:
                                    <strong> “{finding.conflicts_with}”</strong>. Correct that answer or the
                                    record — the check will not ask the same thing again.
                                </div>}
                            {!finding.conflicts_with && !finding.fix_options?.length &&
                                (finding.gap || this.state.answeredBefore[key] !== undefined) &&
                                <div className="emr-check-reasked">
                                    {finding.gap
                                        ? <>What is written is missing <strong>{gapText(finding.gap)}</strong> — {
                                            finding.target_path
                                                ? "edit the text below to include it."
                                                : `add it in ${tab ? tab.label : "the record"}.`}</>
                                        : <>Still not enough after your last answer
                                            (“{formatValue(this.state.answeredBefore[key])}”) — edit the text
                                            below rather than starting again.</>}
                                </div>}
                            {finding.fix_options?.length
                                ? this.renderFixOptions(finding)
                                : finding.target_path
                                    ? <div className="mt-1">{this.renderInput(finding)}</div>
                                    : this.renderGoToTab(tab)}
                            {this.renderSkipControl(finding)}
                        </>
                    )}

                    {finding.reason && <div className="emr-check-reason mt-2">{finding.reason}</div>}
                    {finding.also?.length > 0 &&
                        <div className="emr-check-reason mt-1">
                            Also covers: {finding.also.map(a => a.label).join(", ")}.
                        </div>}
                </>}
            </div>
        );
    }

    renderGoToTab(tab) {
        if (!tab) return null;
        return (
            <Button size="sm" block variant="outline-secondary"
                    onClick={() => this.goToTab(tab.key)}>
                Fix in {tab.label} →
            </Button>
        );
    }

    // Rules that know the fix send it as choices (fix_options): "bill 98940 instead",
    // "document the findings", "no red flags — clear them". One card, mutually exclusive
    // ways out, instead of a question whose answer box has nowhere to go.
    procedureLists = () => [
        ["diagnosis", "selfPayProcedures"],
        ["diagnosis", "directBillingProcedures"],
    ];

    findProcedure = (code) => {
        const visit = this.props.currentVisit;
        for (const path of this.procedureLists()) {
            const list = ObjectUtil.get(visit, path.join(".")) || [];
            const index = list.findIndex(p => String(p?.cpt?.cptCode) === String(code));
            if (index >= 0) return {path, list, index};
        }
        return null;
    };

    fixApplied = (finding, option) => {
        // A one-tap fix re-checks the record at once, so answers typed on other cards go in
        // with it -- otherwise the re-check judges a record that never received them.
        const pending = this.writePending();
        this.setState(state => {
            const next = pending(state);
            return {
                ...next,
                applied: true,
                recommendation: null,
                fixed: {...state.fixed, [findingKey(finding)]: option.label},
                facts: this.withFacts(next.facts, [this.factFor(finding, option.label)]),
            };
        }, () => {
            if (this.props.onApplied) this.props.onApplied();
            this.runAnalyze();
        });
    };

    applyFixOption = (finding, option) => {
        const visit = this.props.currentVisit;
        if (option.type === "go_to") {
            this.goToTab(option.tab);
            return;
        }
        if (option.type === "set") {
            Object.entries(option.values || {}).forEach(([path, value]) =>
                ObjectUtil.set(visit, path.split("."), value));
            this.fixApplied(finding, option);
            return;
        }
        const found = this.findProcedure(option.type === "remove_code" ? option.code : option.from);
        if (!found) return;
        const {path, list, index} = found;
        if (option.type === "remove_code") {
            list.splice(index, 1);
            ObjectUtil.set(visit, path, list);
            this.fixApplied(finding, option);
            return;
        }
        const match = this.state.cptIndex?.get(String(option.to));
        if (!match) return;
        const old = list[index];
        const replacement = {...old, cpt: match.cpt, type: match.billType};
        delete replacement.id;
        const targetPath = match.billType === "DB"
            ? ["diagnosis", "directBillingProcedures"] : ["diagnosis", "selfPayProcedures"];
        list.splice(index, 1);
        ObjectUtil.set(visit, path, list);
        const target = ObjectUtil.get(visit, targetPath.join(".")) || [];
        target.push(replacement);
        ObjectUtil.set(visit, targetPath, target);
        this.fixApplied(finding, option);
    };

    renderFixOptions(finding) {
        const done = this.state.fixed?.[findingKey(finding)];

        return (
            <div className="emr-check-fixes mt-2">
                <div className="emr-check-fixes-title">Choose one:</div>
                {finding.fix_options.map((option, i) => {
                    let disabled = false;
                    let note = null;
                    if (option.type === "replace_code") {
                        if (!this.state.cptIndex) {
                            disabled = true;
                            note = "Checking your catalogue…";
                        } else if (!this.state.cptIndex.get(String(option.to))) {
                            disabled = true;
                            note = `${option.to} is not in this clinic's catalogue.`;
                        }
                    }
                    return (
                        <div key={i} className="mb-1">
                            <Button size="sm" block
                                    variant={option.type === "go_to" ? "outline-secondary" : "outline-primary"}
                                    disabled={disabled}
                                    onClick={() => this.applyFixOption(finding, option)}>
                                {option.label}{option.type === "go_to" ? " →" : ""}
                            </Button>
                            {note && <div className="emr-check-reason">{note}</div>}
                        </div>
                    );
                })}
                {done && <div className="emr-check-reason mt-1">✓ {done} — re-checking…</div>}
            </div>
        );
    }

    renderConfirmations() {
        const open = this.openConfirmations();
        if (open.length === 0) return null;

        return (
            <div className="emr-check-confirm mb-2">
                <div className="emr-check-title">Quick confirmations</div>
                <div className="emr-check-reason mb-1">
                    Not claim findings — one tap each to close the record's open negatives.
                </div>
                {open.map(c =>
                    <Button key={c.key} size="sm" className="mr-1 mb-1"
                            variant={this.state.confirmed[c.key] ? "success" : "outline-secondary"}
                            onClick={() => this.toggleConfirmation(c)}>
                        {this.state.confirmed[c.key] ? "✓ " : ""}{c.label}
                    </Button>
                )}
            </div>
        );
    }

    // Three shapes, because three different things happen to the record and only one of
    // them costs anything:
    //   blank field      Several Times a Day
    //   keep both        Random  + Several Times a Day      (retained value shown plain)
    //   replace          A̶c̶u̶t̶e̶ → Subacute                  (struck through: it is leaving)
    // The strikethrough is reserved for a chosen Replace. Before a decision the old value
    // is shown plain — nothing is leaving until the clinician says so.
    renderComposeValue(row) {
        if (!row.overwrites) {
            return <span className="emr-check-proposed">{formatComposeValue(row.field, row.newValue)}</span>;
        }

        if (row.decision === "keep_both") {
            return <>
                <span className="emr-check-parse-keep">{formatComposeValue(row.field, row.oldValue)}</span>
                <span className="emr-check-parse-plus">+</span>
                <span className="emr-check-proposed">
                    {formatComposeValue(row.field, addedComposeValues(row))}
                </span>
            </>;
        }

        return <>
            <span className={row.decision === "replace" ? "emr-check-old" : "emr-check-parse-keep"}>
                {formatComposeValue(row.field, row.oldValue)}
            </span>
            <span className="emr-check-arrow">→</span>
            <span className="emr-check-proposed">{formatComposeValue(row.field, row.newValue)}</span>
        </>;
    }

    // FE-8: the row states what will happen ("Validated") and offers the two departures from
    // it — correct the value, or drop it. A row that overwrites something the clinician typed
    // still says so, and can keep both values where the field allows it.
    renderComposeRow(row, sectionIndex, rowIndex) {
        const destructive = isDestructiveRow(row);
        const decide = decision => () => this.setComposeRowDecision(sectionIndex, rowIndex, decision);
        const dropped = row.decision === "discard";
        const stateClass = dropped ? "off" : "";

        if (row.editing) {
            return (
                <div key={row.path} className="emr-check-parse-row editing">
                    <span className="emr-check-parse-body">
                        <span className="emr-check-parse-label">{row.label}</span>
                        <TextInput value={row.draftValue ?? ""}
                                   onChange={v => this.setComposeEdit(sectionIndex, rowIndex, v)}/>
                    </span>
                    <span className="emr-check-parse-mode">
                        <button type="button" onClick={() => this.cancelComposeEdit(sectionIndex, rowIndex)}>
                            Cancel
                        </button>
                        <button type="button" className="on"
                                onClick={() => this.commitComposeEdit(sectionIndex, rowIndex)}>
                            Save
                        </button>
                    </span>
                </div>
            );
        }

        return (
            <div key={row.path}
                 className={`emr-check-parse-row ${stateClass} ${destructive ? "destructive" : ""}`}>
                <span className="emr-check-parse-body">
                    <span className="emr-check-parse-label">{row.label}</span>
                    <span className="emr-check-parse-value">{this.renderComposeValue(row)}</span>
                    <span className={`emr-check-parse-status ${dropped ? "dropped" : ""}`}>
                        {dropped ? "Dropped"
                            : row.decision === "keep_both" ? "✓ Kept both"
                            : row.edited ? "✓ Edited" : "✓ Validated"}
                    </span>
                </span>
                <span className="emr-check-parse-mode">
                    {this.isEditableComposeRow(row) && !dropped &&
                        <button type="button"
                                onClick={() => this.startComposeEdit(sectionIndex, rowIndex)}>Edit</button>}
                    {row.overwrites && row.multi && !dropped &&
                        <button type="button" className={row.decision === "keep_both" ? "on" : ""}
                                onClick={decide("keep_both")}>Keep Both</button>}
                    <button type="button" className={dropped ? "on discard" : ""}
                            onClick={decide("discard")}>{dropped ? "Undo" : "Delete"}</button>
                </span>
            </div>
        );
    }

    renderComposeReview() {
        const sections = this.state.composeSections;
        const accepted = this.acceptedComposeRows().length;
        const dropped = sections.flatMap(s => s.rows).filter(r => r.decision === "discard").length;
        const total = sections.reduce((sum, s) => sum + s.rows.length, 0);
        const overwrites = sections.flatMap(s => s.rows).filter(r => r.overwrites).length;
        const additions = total - overwrites;
        const {composeMatched, composeNotReturned} = this.state;

        return (
            <div className="emr-check-parse">
                {/* The header answers the two questions a list of proposed edits raises before
                    any of them can be read: how much of this is new, and how much of it
                    overwrites me. The count is split rather than totalled, because those two
                    kinds of row are reviewed completely differently. */}
                <div className="emr-check-parse-header">
                    <div className="emr-check-parse-header-top">
                        <span className="emr-check-parse-count">{total + composeMatched}</span>
                        <span className="emr-check-parse-headline">
                            field{total + composeMatched === 1 ? "" : "s"} read from your note
                            <span className="emr-check-parse-sub">
                                All validated — read them and Apply. Edit a value that came out
                                wrong, or delete a row you do not want. Nothing is written to the
                                record until you Apply.
                            </span>
                        </span>
                    </div>
                    <div className="emr-check-parse-legend">
                        {additions > 0 &&
                            <span className="emr-check-parse-legend-item">
                                <i className="dot new"/>
                                <b>{additions}</b> add{additions === 1 ? "s" : ""} without removing
                                anything
                            </span>}
                        {overwrites > 0 &&
                            <span className="emr-check-parse-legend-item">
                                <i className="dot over"/>
                                <b>{overwrites}</b> touch{overwrites === 1 ? "es" : ""} a value you
                                already entered
                            </span>}
                        {composeMatched > 0 &&
                            <span className="emr-check-parse-legend-item">
                                <i className="dot same"/>
                                <b>{composeMatched}</b> already on the record — nothing to do
                            </span>}
                    </div>
                </div>

                {sections.map((section, sectionIndex) =>
                    <div key={section.title} className="mb-2">
                        <div className="emr-check-parse-head">
                            <span>{section.title}</span>
                        </div>
                        {section.rows.map((row, rowIndex) => this.renderComposeRow(row, sectionIndex, rowIndex))}
                    </div>
                )}

                {/* The two things a list of changes cannot show. "Agreed with the record" and
                    "the note said nothing about this" both look like absence, and only one of
                    them means the field is safe to leave alone. */}
                <div className="emr-check-parse-summary">
                    {composeNotReturned?.length > 0 &&
                        <div>
                            ○ Not found in the note: {composeNotReturned.join(", ")}. These are
                            untouched — fill them on the form if they apply.
                        </div>}
                    {/* Said in the note, but the form has no field this panel can fill
                        (Mill's test, a grip reading with no slot). Dropping it silently
                        reads as "the note was fully captured". */}
                    {this.state.composeNoSlot?.length > 0 &&
                        <div className="emr-check-parse-noslot">
                            △ In your note but not filled in — add by hand if needed:{" "}
                            {this.state.composeNoSlot.map(u => u.replace(/[.;\s]+$/, "")).join("; ")}.
                        </div>}
                </div>

                {/* Returned by the service but not on this panel's allowlist. Said out loud
                    rather than dropped quietly: the count is what tells us ComposeFields.js
                    needs extending, and it stops the review reading as "that was all". */}
                {this.state.composeUnmapped.length > 0 &&
                    <div className="emr-check-reason mb-2">
                        {this.state.composeUnmapped.length} more field(s) were parsed that this panel
                        cannot review yet ({this.state.composeUnmapped.slice(0, 6).join(", ")}
                        {this.state.composeUnmapped.length > 6 ? ", …" : ""}). Enter those on the form.
                    </div>}

                <div className="emr-check-actions">
                    {dropped > 0 &&
                        <Button block size="sm" variant="outline-secondary" className="mb-1"
                                onClick={this.validateAllCompose}>
                            Restore {dropped} dropped row{dropped === 1 ? "" : "s"}
                        </Button>}
                    <Button block variant="success" onClick={this.applyCompose}>
                        {accepted > 0 ? `Apply ${accepted} field(s)` : "Finish — nothing kept"}
                    </Button>
                    <div className="d-flex justify-content-between mt-1">
                        <Button size="sm" variant="link" className="px-0"
                                onClick={this.generateFromDraft}>Re-generate</Button>
                        <Button size="sm" variant="link" className="px-0"
                                onClick={this.discardCompose}>Discard parse</Button>
                    </div>
                </div>
            </div>
        );
    }

    // One window, three boxes: the accordion keeps everything on one page (no record-tab
    // hopping, no scrolling), and the guideline panel beside it always talks about the
    // box that is open. Dictate top to bottom, then one Generate.
    renderDraftSections() {
        const {draftSections, activeDraftSection} = this.state;
        const combinedText = this.combinedDraftText();
        const sections = [
            {key: "subjective", title: "Subjective",
             placeholder: "What the patient reports — e.g. dull low back pain since 3 weeks, gradual onset, worse on sitting, 6/10, no red flags, no known allergies…"},
            {key: "objective", title: "Objective",
             placeholder: "What you found — e.g. lumbar flexion 40°, SLR negative, palpation unremarkable, gait normal, BP 120/80…"},
            {key: "treatment", title: "Treatment & Plan",
             placeholder: "What you did and the plan — e.g. manual therapy lumbar 15 min, responded well, plan 2x/week for 3 weeks, 6 sessions, goal return to sport…"},
        ];

        return sections.map(section => {
            const open = activeDraftSection === section.key;
            const value = draftSections[section.key] || "";
            return (
                <div key={section.key} className={`emr-check-draft-section ${open ? "open" : ""}`}>
                    <button type="button" className="emr-check-draft-section-head"
                            onClick={() => this.setState({activeDraftSection: section.key})}>
                        <span className="emr-check-draft-section-caret">{open ? "▾" : "▸"}</span>
                        <span className="emr-check-draft-section-title">{section.title}</span>
                        {!open && value.trim() &&
                            <span className="emr-check-draft-section-preview">{value.trim()}</span>}
                    </button>
                    {open && <>
                        <div className="emr-check-draft-wrap">
                            <TextareaInput value={value} className="emr-check-draft"
                                           placeholder={section.placeholder}
                                           onChange={v => this.setDraftSection(section.key, v)}/>
                            {/* The mic sits inside the open box: dictation lands where you are. */}
                            <Dictation onAppend={this.appendDictated}/>
                        </div>
                        {/* The cheat-sheet lives inside the open box, right under where the
                            words land. It still reads the whole draft, not just this box —
                            a fact stated in the "wrong" box counts, same as the parse. */}
                        <GuidelinePanel section={section.key}
                                        text={combinedText}
                                        composedPaths={this.state.composedPaths}/>
                    </>}
                </div>
            );
        });
    }

    renderDraftTab() {
        const text = this.combinedDraftText();

        return (
            <div className="pt-2">
                {this.renderDraftSections()}

                <div className="emr-check-draft-actions">
                    {this.state.composeNote &&
                        <span className="emr-check-reason">{this.state.composeNote}</span>}
                    {/* Not a bootstrap variant: this is the one action on the tab and it is
                        what the drawer is for, so it carries the CoPilot accent rather than
                        looking like every other primary button on the record. */}
                    <button type="button"
                            className={"emr-check-generate" + (this.state.composing ? " busy" : "")}
                            disabled={this.state.composing || !text.trim()}
                            onClick={this.generateFromDraft}>
                        <span className="emr-check-generate-spark">
                            {this.state.composing
                                ? <span className="emr-check-generate-spin"/>
                                : "✦"}
                        </span>
                        {this.state.composing ? "Generating…" : "Generate EMR"}
                    </button>
                </div>

                {this.state.composing && <LoadingSteps messages={COMPOSE_STEPS}/>}
                {!this.state.composing && this.state.composeSections?.length > 0 && this.renderComposeReview()}

                {this.state.applied &&
                    <div className="emr-check-unsaved mt-2">⚠ Applied to the form — not saved yet.</div>}
            </div>
        );
    }

    renderCheckTab() {
        const {loading, error, analysis} = this.state;

        if (loading) return <LoadingSteps messages={CHECK_STEPS}/>;
        if (error) return <div className="p-2 text-danger">{error}</div>;
        if (!analysis) return null;

        const findings = analysis.findings || [];
        const open = findings.filter(f => !this.isSettled(f));
        const pending = this.pendingCount();

        return (
            <div>
                {this.renderHeadline(analysis, open, pending)}

                {findings.length === 0 &&
                    <div className="emr-check-clean">
                        <div className="emr-check-clean-tick">✓</div>
                        <div>
                            <strong>Clean claim</strong>
                            <div className="emr-check-reason">
                                {analysis.checked} rule(s) checked against {
                                    analysis.codes_checked?.length ? "the billed codes" : "the record"} —
                                nothing to fix.
                            </div>
                        </div>
                    </div>}

                {this.renderBulkAnswer()}

                {findings.map(f => this.renderFinding(f, findings))}

                {/* The cap must not read as "that was all": the backend ranks and keeps
                    the highest-impact findings, and says how many it held back. */}
                {analysis.findings_omitted > 0 &&
                    <div className="emr-check-reason mb-2">
                        + {analysis.findings_omitted} lower-priority item(s) held back —
                        fix the above and re-check to see them.
                    </div>}

                {this.renderSkipped()}

                {this.renderConfirmations()}

                {this.state.applied &&
                    <div className="emr-check-unsaved">⚠ Applied to the form — not saved yet.</div>}

                <div className="emr-check-actions">
                    <Button block variant="success" disabled={pending === 0}
                            onClick={() => this.apply(true)}>
                        {pending > 0 ? `Apply ${pending} & Save` : "Nothing to apply"}
                    </Button>
                    <div className="d-flex justify-content-between mt-1">
                        <Button size="sm" variant="link" className="px-0"
                                onClick={this.runAnalyze}>Re-check</Button>
                        <Button size="sm" variant="link" className="px-0" disabled={pending === 0}
                                onClick={() => this.apply(false)}>Apply without saving</Button>
                    </div>
                </div>
            </div>
        );
    }

    // No billed amount and no numeric score in front of the clinician: a "100/100"
    // or an AED figure reads as a promise the insurer will approve, which nobody can
    // make. The clinician gets the three-state headline; the score stays API-only.
    renderHeadline(analysis, open, pending = 0) {
        const blocking = open.filter(f => f.consequence === "deny").length;

        // The bar is the threshold, not zero findings: a record can be good enough to bill
        // with advisory items still open ("instead of satisfying all the rules, we can set a
        // level like 90%"). `ready` comes from the service so both ends agree on the mark.
        const score = analysis.score_excluding_skipped ?? analysis.clean_claim_score;
        const threshold = analysis.threshold ?? 90;
        const ready = analysis.ready ?? (blocking === 0 && score >= threshold);

        let tone = "ok";
        let text = "Documentation supports the claim";
        if (blocking > 0) {
            tone = "bad";
            text = `Claim can be denied — ${blocking} blocking issue${blocking > 1 ? "s" : ""}`;
        } else if (!ready && open.length === 0 && pending > 0) {
            // Answered cards count as closed, but the score is from before the answers:
            // "0 issues weakening the claim" read as a contradiction.
            tone = "warn";
            text = "All answered — apply to re-check";
        } else if (!ready) {
            tone = "warn";
            text = `${open.length} issue${open.length === 1 ? "" : "s"} weakening the claim`;
        } else if (open.length > 0) {
            text = `Good enough to bill — ${open.length} advisory item${open.length === 1 ? "" : "s"} left`;
        }

        return (
            <div className={`emr-check-headline ${tone}`}>
                <div className="emr-check-headline-text">{text}</div>
                <div className="emr-check-headline-sub">
                    {/* No percentage: the host EMR already shows its own "EMR Status" ring, and
                        two numbers measuring different things side by side read as a contradiction
                        (73% vs 100/100, 2026-09-17). The state and the blocking count are the
                        whole message; the score stays in the API. */}
                    {analysis.skipped_count > 0 &&
                        <span>{analysis.skipped_count} marked not applicable</span>}
                    {tone === "ok" &&
                        <span className={analysis.skipped_count > 0 ? "ml-2" : ""}>
                            {analysis.skipped_count > 0 ? "· " : ""}documentation check only, not a guarantee of
                            insurance approval</span>}
                    {analysis.semantic === false && <span className="ml-2">(quick check)</span>}
                </div>
            </div>
        );
    }

    // Both engines answer the same question, so they belong in one list; where a code
    // came from is a property of the row (a badge), not a reason to split the screen.
    // Codes already on the record are dropped entirely — the tab is only about what the
    // clinician has NOT recorded yet.
    mergeRecommendations(lists) {
        const recorded = recordedCptCodes(this.props.currentVisit);
        const merged = new Map();

        lists.forEach(({items, source}) => {
            (items || []).forEach(item => {
                const code = String(item?.code || "").trim();
                if (!code || code === "N/A" || recorded.has(code)) return;
                // The two engines can name the same diagnosis at different depths --
                // similar visits answered M54.5 where the AI answered M54.50 -- and keying
                // on the literal string put both on screen as separate suggestions ("these
                // two texts are same"). Fold a code into its parent/child and keep the more
                // specific one, since that is the one a claim can carry.
                const key = mergeKeyFor(code, merged);
                const existing = merged.get(key) || {code, sources: []};
                if (key !== code && isMoreSpecific(code, existing.code)) existing.code = code;
                merged.set(key, {
                    ...existing,
                    description: existing.description || item.description || item.name || "",
                    reason: existing.reason || item.reason,
                    is_valid: item.is_valid === false ? false : existing.is_valid,
                    validation_warning: existing.validation_warning || item.validation_warning,
                    code: existing.code || code,
                    sources: existing.sources.includes(source)
                        ? existing.sources : [...existing.sources, source],
                });
            });
        });

        return [...merged.values()];
    }

    // Which list a new procedure belongs in. Insurance decides first: the catalogue
    // endpoint returns nothing for DIRECT_BILLING scope unless insurance/company is
    // passed, so an uninsured patient can only be billed self-pay anyway. With insurance,
    // follow whichever table this visit already uses.
    billTypeForVisit() {
        if (!ObjectUtil.get(this.props.emr, "patient.insurance")) return "SP";
        const visit = this.props.currentVisit;
        if ((ObjectUtil.get(visit, "diagnosis.selfPayProcedures") || []).length > 0
            && (ObjectUtil.get(visit, "diagnosis.directBillingProcedures") || []).length === 0) {
            return "SP";
        }
        return "DB";
    }

    // A procedure must carry the real catalogue CptDTO — the backend resolves it by
    // cpt.id, so a {code, description} stub from the recommender cannot be saved.
    // Same endpoint and params the Diagnosis tab's dropdown uses.
    //
    // Both scopes are loaded, not just the preferred one: the recommender knows nothing
    // about this clinic's price list, so a code may exist only self-pay or only under the
    // patient's insurance. Whichever list actually carries the code decides the table it
    // goes into. (DIRECT_BILLING returns nothing without insurance params, so that call
    // is skipped for uninsured patients rather than firing and coming back empty.)
    loadCptCatalog = () => {
        if (this.state.cptIndex || this.state.catalogLoading) return;
        this.setState({catalogLoading: true});

        const visit = this.props.currentVisit;
        const country = ObjectUtil.get(visit, "clinic.country");
        const speciality = ObjectUtil.get(visit, "appointment.staff.specialityName");
        const insurance = ObjectUtil.get(this.props.emr, "patient.insurance");
        const url = `api/cpt/code-or-description/${country}`;

        const queries = [{
            billType: "SP",
            params: {scopes: ["ALL", "SELF_PAY"], speciality},
        }];
        if (insurance) {
            queries.push({
                billType: "DB",
                params: {
                    filterWebinarAndClass: true,
                    scopes: ["ALL", "DIRECT_BILLING"],
                    speciality,
                    insurance: insurance?.tpa?.name,
                    insuranceCompanyId: insurance?.company?.id,
                },
            });
        }

        Promise.all(queries.map(q => new Promise(resolve =>
            RemotingService.getRemoteCall(url, q.params,
                catalog => resolve({billType: q.billType, catalog: catalog || []}),
                () => resolve(null))
        ))).then(results => {
            if (results.every(r => r === null)) {
                this.setState({catalogLoading: false, addError: "Could not load the CPT catalogue."});
                return;
            }
            const index = new Map();
            // Insured patients bill through DB when the code exists there, so let the DB
            // pass overwrite the self-pay entry.
            results.filter(Boolean).forEach(({billType, catalog}) =>
                catalog.forEach(cpt => index.set(String(cpt.cptCode), {cpt, billType})));
            this.setState({cptIndex: index, catalogLoading: false});
        });
    };

    addProcedure = (item) => {
        const match = this.state.cptIndex?.get(String(item.code));
        if (!match) return;      // the row renders a note instead of a button in this case

        const listPath = match.billType === "DB"
            ? ["diagnosis", "directBillingProcedures"]
            : ["diagnosis", "selfPayProcedures"];
        const visit = this.props.currentVisit;
        const list = ObjectUtil.get(visit, listPath.join(".")) || [];
        // Not approved by default: "select for billing" stays the clinician's call,
        // and only approved procedures get service times and treatment mapping.
        list.push({type: match.billType, quantity: 1, approved: false, cpt: match.cpt});
        ObjectUtil.set(visit, listPath, list);

        this.codesChanged = true;
        this.setState({applied: true});
        if (this.props.onApplied) this.props.onApplied();
    };

    // The recommender proposes codes from clinical similarity; it cannot know this
    // clinic's price list. Say so up front instead of offering a button that fails —
    // e.g. 97140 exists for Physiotherapist and Chiropractor but not for a Physical
    // Medicine & Rehabilitation Consultant, whose catalogue simply does not carry it.
    renderAddAction(item) {
        if (this.state.catalogLoading || !this.state.cptIndex) {
            return <div className="emr-check-reason mt-2">Checking your catalogue…</div>;
        }

        const match = this.state.cptIndex.get(String(item.code));
        if (!match) {
            const speciality = ObjectUtil.get(this.props.currentVisit, "appointment.staff.specialityName");
            return (
                <div className="emr-check-reason mt-2">
                    Not billable here — {item.code} is not in the {speciality || "provider"} catalogue
                    for this clinic.
                </div>
            );
        }

        // The catalogue is the authority on what can be billed. `is_valid` only says the
        // code was not in the recommender's training data (98940 was missing while 98942
        // was there), so it warns on the row but must not lock the button.
        return (
            <Button size="sm" block className="mt-2" variant="outline-success"
                    onClick={() => this.addProcedure(item)}>
                Add if performed ({match.billType === "DB" ? "direct billing" : "self-pay"})
            </Button>
        );
    }

    renderCodeRow(item, {addable} = {}) {
        return (
            <div key={item.code} className="emr-check-card audit">
                <div className="emr-check-head">
                    <span className="emr-check-label">{item.code}</span>
                    {item.sources.map(s =>
                        <Badge key={s} pill variant="light" className="emr-check-source">{s}</Badge>)}
                    {item.is_valid === false && !item.validation_warning &&
                        <Badge pill variant="warning">Not seen before</Badge>}
                </div>
                <div className="mt-1">{item.description}</div>
                {item.reason && <div className="emr-check-reason mt-1">{item.reason}</div>}
                {item.validation_warning &&
                    <div className="emr-check-reason text-warning">{item.validation_warning}</div>}
                {addable && this.renderAddAction(item)}
            </div>
        );
    }

    // "Not enough clinical data" + a bare field name is a dead end: it names an API field,
    // not something on the clinician's screen, and offers nothing to do about it. Say which
    // note is missing, in the record's own words, and open the place it is written.
    renderCptUnavailable(recommendation) {
        const missing = (recommendation.required_fields || []).map(f => REQUIRED_FIELD_META[f]
            || {label: f.replace(/_/g, " "), tab: null});

        return (
            <div className="pt-2">
                <div className="emr-check-empty">
                    <div className="emr-check-empty-mark">◎</div>
                    <div>
                        <strong>Not enough written down yet to suggest codes</strong>
                        <div className="emr-check-reason">
                            The recommender works from what the visit says. It still needs:
                        </div>
                    </div>
                </div>

                {missing.map(item =>
                    <div key={item.label} className="emr-check-card audit">
                        <div className="emr-check-head">
                            <span className="emr-check-label">{item.label}</span>
                        </div>
                        {item.hint && <div className="emr-check-reason mt-1">{item.hint}</div>}
                        {item.tab
                            ? <Button size="sm" block variant="outline-secondary" className="mt-2"
                                      onClick={() => this.goToTab(item.tab)}>
                                Write it in {SECTION_TAB_LABEL[item.tab] || item.tab}
                            </Button>
                            : <Button size="sm" block variant="outline-secondary" className="mt-2"
                                      onClick={() => this.setState({activeTab: "draft"})}>
                                Dictate it in Today's Visit
                            </Button>}
                    </div>)}

                <div className="emr-check-actions">
                    <Button block variant="outline-primary" onClick={this.loadRecommendation}>
                        Check again
                    </Button>
                </div>
            </div>
        );
    }

    renderCptTab() {
        const {recommendLoading, recommendError, recommendation, analysis} = this.state;

        if (recommendLoading) return <LoadingSteps messages={CPT_STEPS}/>;
        if (recommendError) {
            return (
                <div className="emr-check-empty">
                    <div className="emr-check-empty-mark">!</div>
                    <div>
                        <strong>Could not reach the recommender</strong>
                        <div className="emr-check-reason">{recommendError}</div>
                        <Button size="sm" variant="outline-secondary" className="mt-2"
                                onClick={this.loadRecommendation}>Try again</Button>
                    </div>
                </div>
            );
        }
        // Nothing to show and nothing in flight: a state the tab can legitimately reach (the
        // record reloaded under it), so it offers the action instead of rendering blank.
        if (!recommendation) {
            return (
                <div className="emr-check-empty">
                    <div className="emr-check-empty-mark">◎</div>
                    <div>
                        <strong>No recommendations yet</strong>
                        <div className="emr-check-reason">
                            The record changed, so the previous suggestions were dropped.
                        </div>
                        <Button size="sm" variant="outline-primary" className="mt-2"
                                onClick={this.loadRecommendation}>Get recommendations</Button>
                    </div>
                </div>
            );
        }

        if (recommendation.unavailable) return this.renderCptUnavailable(recommendation);

        const openFindings = (analysis?.findings || []).length;
        const shown = lists => lists.filter(l => SHOW_SIMILAR_VISITS || l.source !== SIMILAR_SOURCE);
        const procedures = this.mergeRecommendations(shown([
            {items: recommendation.direct_billing_procedures, source: SIMILAR_SOURCE},
            {items: recommendation.direct_billing_procedures_ai, source: "AI"},
        ]));
        const diagnoses = this.mergeRecommendations(shown([
            {items: recommendation.diagnoses, source: SIMILAR_SOURCE},
            // diagnoses_ai is a single object, not a list
            {items: recommendation.diagnoses_ai ? [recommendation.diagnoses_ai] : [], source: "AI"},
        ]));

        // A code this clinic cannot bill for this provider is not a suggestion (98940 for a
        // physiotherapist, 2026-09-17): one line names it instead of a full card with three
        // warnings. Until the catalogue loads every row stays, marked "Checking…".
        const index = this.state.cptIndex;
        const notInCatalogue = index ? procedures.filter(p => !index.get(String(p.code))) : [];
        const billable = index ? procedures.filter(p => index.get(String(p.code))) : procedures;

        return (
            <div className="pt-2">
                {openFindings > 0 &&
                    <div className="emr-check-unsaved">
                        ⚠ {openFindings} open documentation issue(s) — these suggestions rest on an
                        incomplete record.
                    </div>}

                {this.state.addError && <div className="text-danger mb-2">{this.state.addError}</div>}
                {this.state.applied &&
                    <div className="emr-check-unsaved">⚠ Added to the record — not saved yet.</div>}

                {/* Diagnosis first, procedures after (2026-08-17 review): the diagnosis
                    is what grounds every procedure's medical necessity, so it is the
                    decision to make before adding codes. */}
                {diagnoses.length > 0 && <div className="emr-check-title mt-1">Diagnoses not yet recorded</div>}
                {diagnoses.map(d => this.renderCodeRow(d))}
                {diagnoses.length > 0 &&
                    <div className="emr-check-reason">Add these in Diagnosis & Billing — a claim needs
                        a principal diagnosis, which is the clinician's call.</div>}

                {billable.length > 0 && <div className="emr-check-title mt-2">Procedures not yet recorded</div>}
                {billable.map(p => this.renderCodeRow(p, {addable: true}))}
                {notInCatalogue.length > 0 &&
                    <div className="emr-check-reason mt-1">
                        Not shown — not in the {ObjectUtil.get(this.props.currentVisit, "appointment.staff.specialityName")
                            || "provider"} catalogue for this clinic: {notInCatalogue.map(p => p.code).join(", ")}.
                    </div>}

                {billable.length === 0 && diagnoses.length === 0 &&
                    <div className="emr-check-clean">
                        <div className="emr-check-clean-tick">✓</div>
                        <div>
                            <strong>Nothing missing</strong>
                            <div className="emr-check-reason">
                                Every recommended code is already on this visit.
                            </div>
                        </div>
                    </div>}

                <div className="emr-check-actions">
                    <Button block variant="success" disabled={!this.state.applied}
                            onClick={() => this.props.onSaveRequested && this.props.onSaveRequested()}>
                        Save visit
                    </Button>
                </div>
            </div>
        );
    }

    // What the clinician actually had on screen when they hit the thumb. The heavy
    // inputs (the note, the record, the prompts) are already in the service's event
    // log under the request ids we send alongside — this is the *view*: which answers
    // were shown, and what the clinician had done with them by then.
    feedbackSnapshot = () => {
        const {analysis, composeSections, recommendation, activeTab, draftSections} = this.state;

        const findings = (analysis?.findings || []).map(f => ({
            rule_id: f.rule_id,
            kind: f.kind,
            rule_kind: f.rule_kind,
            consequence: f.consequence,
            target_path: f.target_path,
            question: f.question || f.message,
            answered: this.state.answers[findingKey(f)] !== undefined,
            accepted: !!this.state.accepted[findingKey(f)],
        }));

        const composeRows = (composeSections || []).flatMap(section =>
            section.rows.map(r => ({path: r.path, decision: r.decision, overwrites: !!r.overwrites})));

        const codeList = items => (items || []).map(i => i.code);

        return {
            tab: activeTab,
            draft_lengths: {
                subjective: (draftSections.subjective || "").length,
                objective: (draftSections.objective || "").length,
                treatment: (draftSections.treatment || "").length,
            },
            check: analysis ? {
                clean_claim_score: analysis.clean_claim_score,
                blocking_count: analysis.blocking_count,
                checked: analysis.checked,
                codes_checked: analysis.codes_checked,
                findings_omitted: analysis.findings_omitted,
                semantic: analysis.semantic,
                findings,
            } : null,
            compose: composeSections ? {
                rows: composeRows,
                unmapped: this.state.composeUnmapped,
                no_slot: this.state.composeNoSlot,
                note: this.state.composeNote,
            } : null,
            cpt: recommendation ? {
                unavailable: recommendation.unavailable || null,
                diagnoses: codeList(recommendation.diagnoses),
                diagnoses_ai: recommendation.diagnoses_ai ? [recommendation.diagnoses_ai.code] : [],
                procedures: codeList(recommendation.direct_billing_procedures),
                procedures_ai: codeList(recommendation.direct_billing_procedures_ai),
                similar_shown: SHOW_SIMILAR_VISITS,
            } : null,
            applied: this.state.applied,
            errors: [this.state.error, this.state.recommendError, this.state.addError].filter(Boolean),
        };
    };

    // The finding count rides on the tab, not just the button that opened the drawer:
    // the drawer lands on Draft, so Check has to advertise its own work from across the room.
    renderCheckTabTitle() {
        const findings = this.state.analysis?.findings || [];
        const open = findings.filter(f => !this.isSettled(f)).length;
        return (
            <>
                EMR Check
                {open > 0 && <span className="badge badge-pill badge-danger ml-1">{open}</span>}
            </>
        );
    }

    // A new parse, a new check or a new code list is a new thing to judge.
    feedbackResetKey = () => [
        this.state.activeTab,
        this.state.analysis ? this.state.analysis.clean_claim_score : "-",
        (this.state.analysis?.findings || []).length,
        this.state.composeSections ? this.state.composeSections.length : "-",
        this.state.recommendation ? "cpt" : "-",
    ].join("|");

    currentUserLabel = () => {
        try {
            const user = AuthService.getUser();
            return user ? (user.username || user.email || String(user.id)) : null;
        } catch (e) {
            return null;   // never let telemetry break the drawer
        }
    };

    render() {
        const {wide} = this.state;

        // Hidden rather than unmounted: this component keeps its state (the note, the parse,
        // the review ticks) while it renders nothing. See requestClose.
        if (this.props.hidden) return null;

        return (
            <div className={`emr-check-panel ${wide ? "wide" : ""}`} style={{top: this.state.topOffset}}>
                <div className="emr-check-drawer-header">
                    <span className="emr-check-title">EMR CoPilot</span>
                    <span>
                        <button type="button" className="emr-check-close mr-1"
                                title={wide ? "Narrow the drawer" : "Widen the drawer"}
                                onClick={() => this.setState(s => ({wide: !s.wide}))}>
                            {wide ? "⇥" : "⇤"}
                        </button>
                        <button type="button" className="emr-check-close" onClick={this.requestClose}>
                            Close ✕
                        </button>
                    </span>
                </div>

                <div className="emr-check-drawer-body">
                    <Tabs id="emr-check-tabs" activeKey={this.state.activeTab} onSelect={this.onSelectTab}>
                        {/* "Today's Visit", not "Draft": the tab name is the instruction —
                            write (or dictate) what happened today, then Generate. */}
                        <Tab eventKey="draft" title="Today's Visit">{this.renderDraftTab()}</Tab>
                        <Tab eventKey="check" title={this.renderCheckTabTitle()}>{this.renderCheckTab()}</Tab>
                        <Tab eventKey="cpt" title="CPT Recommender">{this.renderCptTab()}</Tab>
                    </Tabs>
                </div>

                {/* Pinned outside the scrolling body: the verdict has to be reachable from
                    the bottom of a long finding list without scrolling back up. resetKey
                    changes whenever a fresh answer lands, so a thumb is asked once per
                    answer rather than once per visit. */}
                <FeedbackBar surface={this.state.activeTab}
                             resetKey={this.feedbackResetKey()}
                             visitId={ObjectUtil.get(this.props.currentVisit, "id")}
                             mrn={ObjectUtil.get(this.props.emr, "patient.mrn.mrnNo")
                                 || ObjectUtil.get(this.props.emr, "patient.mrn.id")}
                             specialty={ObjectUtil.get(this.props.currentVisit, "appointment.staff.specialityName")}
                             clinic={ObjectUtil.get(this.props.currentVisit, "appointment.clinic.name")}
                             user={this.currentUserLabel()}
                             getSnapshot={this.feedbackSnapshot}/>
            </div>
        );
    }
}
